import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import styles from "../styles/auth-pages.module.css";

const normalizeOtpValue = (value, length) => (value ?? "")
    .replace(/\D/g, "")
    .slice(0, length);

const OtpCodeInput = forwardRef(function OtpCodeInput({
    value = "",
    length = 6,
    disabled = false,
    isInvalid = false,
    success = false,
    autoFocus = false,
    focusKey = "default",
    onChange,
    onComplete
}, ref) {
    const inputRef = useRef(null);
    const inputId = useId();
    const normalizedValueRef = useRef("");
    const [isFocused, setIsFocused] = useState(false);
    const normalizedValue = normalizeOtpValue(value, length);
    const characters = Array.from({ length }, (_, index) => normalizedValue[index] ?? "");
    const focusedDisplayIndex = Math.min(normalizedValue.length, length - 1);

    useEffect(() => {
        normalizedValueRef.current = normalizedValue;
    }, [normalizedValue]);

    const setCursorPosition = useCallback((index) => {
        const input = inputRef.current;

        if (!input) {
            return;
        }

        const nextIndex = Math.max(0, Math.min(index, length));

        try {
            input.setSelectionRange(nextIndex, nextIndex);
        } catch {
            // Some mobile browsers can reject selection changes during autofill.
        }
    }, [length]);

    const focusInput = useCallback((index) => {
        if (disabled) {
            return;
        }

        window.requestAnimationFrame(() => {
            inputRef.current?.focus();
            setCursorPosition(index);
        });
    }, [disabled, setCursorPosition]);

    const focusFirstEmpty = useCallback(() => {
        const currentValue = normalizedValueRef.current;
        const firstEmptyIndex = Array.from({ length }, (_, index) => currentValue[index] ?? "")
            .findIndex((character) => !character);
        const focusIndex = firstEmptyIndex === -1 ? length : firstEmptyIndex;
        focusInput(focusIndex);
    }, [focusInput, length]);

    useImperativeHandle(ref, () => ({
        focusFirstEmpty
    }), [focusFirstEmpty]);

    useEffect(() => {
        if (!autoFocus || disabled) {
            return;
        }

        const focusTimer = window.setTimeout(() => {
            focusFirstEmpty();
        }, 25);

        return () => window.clearTimeout(focusTimer);
    }, [autoFocus, disabled, focusFirstEmpty, focusKey]);

    const emitValue = useCallback((nextValue, nextFocusIndex = null) => {
        const normalizedNextValue = normalizeOtpValue(nextValue, length);
        onChange?.(normalizedNextValue);

        if (typeof nextFocusIndex === "number") {
            window.setTimeout(() => {
                setCursorPosition(nextFocusIndex);
            }, 0);
        }

        if (normalizedNextValue.length === length) {
            window.setTimeout(() => {
                onComplete?.(normalizedNextValue);
            }, 0);
        }
    }, [length, onChange, onComplete, setCursorPosition]);

    const handleChange = (event) => {
        const nextValue = normalizeOtpValue(event.target.value, length);
        emitValue(nextValue, nextValue.length);
    };

    const handlePaste = (event) => {
        const pastedDigits = normalizeOtpValue(event.clipboardData.getData("text"), length);

        if (!pastedDigits) {
            return;
        }

        event.preventDefault();

        if (pastedDigits.length >= length) {
            emitValue(pastedDigits, length);
            return;
        }

        const currentValue = normalizedValueRef.current;
        const selectionStart = event.currentTarget.selectionStart ?? currentValue.length;
        const selectionEnd = event.currentTarget.selectionEnd ?? selectionStart;
        const nextValue = `${currentValue.slice(0, selectionStart)}${pastedDigits}${currentValue.slice(selectionEnd)}`;
        const nextFocusIndex = Math.min(selectionStart + pastedDigits.length, length);

        emitValue(nextValue, nextFocusIndex);
    };

    return (
        <div className={styles.otpRow} role="group" aria-label="One-time code" onClick={focusFirstEmpty}>
            <input
                id={inputId}
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="done"
                className={styles.otpAutofillInput}
                value={normalizedValue}
                maxLength={length}
                disabled={disabled}
                aria-label="One-time code"
                aria-invalid={isInvalid || undefined}
                onChange={handleChange}
                onPaste={handlePaste}
                onFocus={() => {
                    setIsFocused(true);
                    focusFirstEmpty();
                }}
                onBlur={() => setIsFocused(false)}
                onClick={focusFirstEmpty}
            />
            {characters.map((character, index) => (
                <span
                    key={index}
                    className={[
                        styles.otpInput,
                        character ? styles.otpInputFilled : "",
                        isFocused && index === focusedDisplayIndex && !success && !disabled ? styles.otpInputFocused : "",
                        isInvalid ? styles.otpInputInvalid : "",
                        success ? styles.otpInputSuccess : "",
                        disabled ? styles.otpInputDisabled : ""
                    ].filter(Boolean).join(" ")}
                    aria-hidden="true"
                >
                    {character}
                </span>
            ))}
        </div>
    );
});

export default OtpCodeInput;
